import { useCallback, useMemo } from 'react';

import { useInterpret, useSelector } from '@xstate/react';

import { createScanToPayMachine } from './scan-to-pay.machine';

import type {
  ScanToPayMachineContext,
  ScanToPayMachineServices,
} from './scan-to-pay.machine.types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A hook to provide an interface to communicate with the Scan to Pay state machine.
 */
export const useScanToPayMachine = (params: UseScanToPayMachineParams) => {
  const { fetchData, fetchIdentifyToken, fetchCredits, fetchPaymentToken } = params;

  // ─── State Machine ───────────────────────────────────────────────────

  // eslint-disable-next-line functional/prefer-tacit
  const scanToPayMachine = useMemo(() => createScanToPayMachine(), []);

  // ─── Machine Service ─────────────────────────────────────────────────

  const service = useInterpret(scanToPayMachine, {
    services: {
      fetchData,
      fetchIdentifyToken,
      fetchPaymentToken,
      fetchCredits,
    },
  });

  // ─── Context ─────────────────────────────────────────────────────────

  const context = useSelector(service, (currentState) => currentState.context);

  // ─── Flags ───────────────────────────────────────────────────────────

  const isScanToPayFailed = useSelector(service, (currentState) => {
    const { matches } = currentState;

    return matches('failed');
  });

  const isFetchingData = useSelector(service, (currentState) => {
    const { matches } = currentState;

    return (
      matches('fetching') ||
      matches('ready.paymentToken.fetching') ||
      matches('ready.identifyToken.fetching')
    );
  });

  const isShowingLoyaltyInfo = useSelector(service, (currentState) => {
    const { matches } = currentState;

    return matches('ready.standard.loyaltyInfo');
  });

  const isShowingFaq = useSelector(service, (currentState) => {
    const { matches } = currentState;

    return matches('ready.standard.faq');
  });

  // ─── Actions ─────────────────────────────────────────────────────────

  const start = useCallback(() => {
    service.send({ type: 'START' });
  }, [service]);

  const cancelRequest = useCallback(() => {
    service.send({ type: 'CANCEL_REQUEST' });
  }, [service]);

  const switchUseCredit = useCallback(() => {
    service.send({ type: 'SWITCH_USE_CREDIT' });
  }, [service]);

  const requestLoyaltyInfo = useCallback(() => {
    service.send({ type: 'REQUEST_LOYALTY_INFO' });
  }, [service]);

  const refetchPaymentToken = useCallback(() => {
    service.send({ type: 'REFETCH_PAYMENT_TOKEN' });
  }, [service]);

  const refetchIdentifyToken = useCallback(() => {
    service.send({ type: 'REFETCH_IDENTIFY_TOKEN' });
  }, [service]);

  const refetchCredits = useCallback(() => {
    service.send({ type: 'REFETCH_CREDITS' });
  }, [service]);

  const requestFaq = useCallback(() => {
    service.send({ type: 'REQUEST_FAQ' });
  }, [service]);

  // ─── Response ────────────────────────────────────────────────────────

  return useMemo(
    () => ({
      ...context,
      isScanToPayFailed,
      isFetchingData,
      isShowingLoyaltyInfo,
      isShowingFaq,
      start,
      cancelRequest,
      switchUseCredit,
      requestLoyaltyInfo,
      refetchPaymentToken,
      refetchIdentifyToken,
      refetchCredits,
      requestFaq,
    }),
    [
      context,
      isScanToPayFailed,
      isFetchingData,
      isShowingLoyaltyInfo,
      isShowingFaq,
      start,
      cancelRequest,
      switchUseCredit,
      requestLoyaltyInfo,
      refetchPaymentToken,
      refetchIdentifyToken,
      refetchCredits,
      requestFaq,
    ],
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseScanToPayMachineParams = {
  fetchData: () => Promise<ScanToPayMachineServices['fetchData']['data']>;
  fetchIdentifyToken: () => Promise<ScanToPayMachineServices['fetchIdentifyToken']['data']>;
  fetchPaymentToken: (
    context: ScanToPayMachineContext,
  ) => Promise<ScanToPayMachineServices['fetchPaymentToken']['data']>;
  fetchCredits: () => Promise<{ availableCredit: number }>;
};
